/**
 * ActivityWatchlist.js
 *
 * Per-admin tracker for member activity. One watchlist item == one BonusClub
 * person. For each watched person we show, per account/world they belong to:
 *   - how many emails they sent (attributed via sentByUserId)
 *   - real active time on the platform (heartbeat-derived)
 *   - session count + when the sessions were
 *   - a per-day timeline
 *
 * Storage: admin_watchlists/{adminUid}/items/{itemId} (Firestore)
 *   - ownerUserId (== personUid), ownerEmail, ownerName
 *   - dailyTarget (sent mails / day across all accounts)
 *   - addedBy, createdAt
 *
 * Stats: getMemberActivityStats Cloud Function (admin-only), reading the tiny
 * member_activity/{personUid}/days/{day} rollups. Polled every 60s while the
 * tab is visible.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auth, firestore, database, functions } from '../firebaseConfig';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { ref as dbRef, get } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import './ActivityWatchlist.css';

const POLL_INTERVAL_MS = 60_000;
const WINDOW_DAYS = 14;

function classifyProgress(sent, target) {
  if (!target || target <= 0) return 'neutral';
  const pct = sent / target;
  if (pct >= 1) return 'green';
  if (pct >= 0.8) return 'yellow';
  return 'red';
}

function formatDay(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit' });
}

function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

function formatSessionRange(start, end) {
  if (!start) return '';
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const day = s.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  const startT = s.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const endT = e ? e.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '…';
  return `${day} · ${startT}–${endT}`;
}

const ROLE_LABELS = {
  owner: 'Eigener Account',
  master: 'Master Member',
  standard: 'Member',
  member: 'Member',
  subworld: 'Sub-World',
};

function ProgressDonut({ sent, target, size = 120 }) {
  const status = classifyProgress(sent, target);
  const pct = target > 0 ? Math.min(sent / target, 1.5) : 0;
  const visualPct = Math.min(pct, 1) * 100;
  const overshoot = pct > 1;

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (visualPct / 100) * circumference;

  return (
    <div className={`watchlist-donut watchlist-donut-${status}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 120 120" className="watchlist-donut-svg">
        <circle cx="60" cy="60" r={radius} className="watchlist-donut-track" />
        <circle
          cx="60"
          cy="60"
          r={radius}
          className="watchlist-donut-progress"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 60 60)"
        />
      </svg>
      <div className="watchlist-donut-content">
        <div className="watchlist-donut-sent">{sent}</div>
        <div className="watchlist-donut-target">/ {target || '—'}</div>
        {overshoot && <div className="watchlist-donut-overshoot">+{sent - target}</div>}
      </div>
    </div>
  );
}

/** Generic per-day bars. `metric` is 'sent' or 'activeSeconds'. */
function ActivityBars({ days = [], metric = 'sent' }) {
  const max = Math.max(1, ...days.map((d) => d[metric] || 0));
  return (
    <div className="watchlist-acct-bars">
      {days.map((d) => {
        const val = d[metric] || 0;
        const barH = Math.max(2, (val / max) * 100);
        const label =
          metric === 'activeSeconds' ? formatDuration(val) : `${val}`;
        return (
          <div
            key={d.date}
            className="watchlist-acct-bar-col"
            title={`${d.date}: ${metric === 'activeSeconds' ? formatDuration(val) : `${val} gesendet`}`}
          >
            <div className="watchlist-acct-bar-stack">
              <div
                className={`watchlist-acct-bar watchlist-acct-bar-${metric}`}
                style={{ height: `${barH}%` }}
              />
            </div>
            <div className="watchlist-acct-bar-label">{formatDay(d.date).split(' ')[1]}</div>
          </div>
        );
      })}
    </div>
  );
}

/** One day in the aggregated platform timeline, expandable into per-account categories. */
function DayBreakdownRow({ day }) {
  const [open, setOpen] = useState(false);
  const accountList = day.byAccount || [];
  const expandable = accountList.length >= 1;
  const multiAccount = accountList.length > 1;
  const dateObj = new Date(day.date + 'T00:00:00Z');
  const weekday = dateObj.toLocaleDateString('de-DE', { weekday: 'long' });
  const dateLabel = dateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

  return (
    <div className={`watchlist-day-row ${open ? 'watchlist-day-row-open' : ''}`}>
      <button
        className="watchlist-day-row-head"
        onClick={() => expandable && setOpen((v) => !v)}
        disabled={!expandable}
      >
        <div className="watchlist-day-row-date">
          {expandable && (
            <i className={`fa fa-chevron-${open ? 'down' : 'right'} watchlist-day-row-chevron`}></i>
          )}
          <span className="watchlist-day-row-day">{dateLabel}</span>
          <span className="watchlist-day-row-weekday">{weekday}</span>
          {multiAccount && (
            <span className="watchlist-day-row-multi">{accountList.length} Accounts</span>
          )}
        </div>
        <div className="watchlist-day-row-stats">
          <span className="watchlist-day-stat watchlist-day-stat-active">
            <i className="fa fa-clock"></i> {formatDuration(day.activeSeconds)}
          </span>
          <span className="watchlist-day-stat watchlist-day-stat-sent">
            <i className="fa fa-paper-plane"></i> {day.sent}
          </span>
          <span className="watchlist-day-stat watchlist-day-stat-sessions">
            <i className="fa fa-bolt"></i> {day.sessionCount}
          </span>
        </div>
      </button>

      {open && expandable && (
        <div className="watchlist-day-split">
          {accountList.map((acc) => {
            const activeShare =
              day.activeSeconds > 0
                ? Math.round((acc.activeSeconds / day.activeSeconds) * 100)
                : 0;
            return (
              <div key={acc.worldUid} className="watchlist-day-split-item">
                <div className="watchlist-day-split-top">
                  <span className="watchlist-day-split-name">
                    <span
                      className={`watchlist-day-split-dot ${acc.isOwn ? 'own' : 'member'}`}
                    ></span>
                    <span className="watchlist-day-split-world">{acc.worldName}</span>
                    <span className="watchlist-day-split-role">
                      {ROLE_LABELS[acc.role] || 'Member'}
                    </span>
                  </span>
                  <span className="watchlist-day-split-vals">
                    <span className="watchlist-day-split-val watchlist-day-stat-active">
                      <i className="fa fa-clock"></i> {formatDuration(acc.activeSeconds)}
                    </span>
                    <span className="watchlist-day-split-val watchlist-day-stat-sent">
                      <i className="fa fa-paper-plane"></i> {acc.sent}
                    </span>
                    <span className="watchlist-day-split-val watchlist-day-stat-sessions">
                      <i className="fa fa-bolt"></i> {acc.sessionCount || 0}
                    </span>
                  </span>
                </div>
                {multiAccount && (
                  <div
                    className="watchlist-day-split-bar"
                    title={`${activeShare}% der Aktivzeit`}
                  >
                    <div
                      className={`watchlist-day-split-bar-fill ${acc.isOwn ? 'own' : 'member'}`}
                      style={{ width: `${activeShare}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Aggregated 14-day platform timeline for a person (summed over all accounts). */
function PersonTimeline({ days = [], totals = {} }) {
  const activeDays = days.filter((d) => d.sent > 0 || d.activeSeconds > 0);
  const activeDayCount = activeDays.length;
  const avgActivePerActiveDay = activeDayCount
    ? Math.round((totals.activeSeconds || 0) / activeDayCount)
    : 0;
  const avgSentPerActiveDay = activeDayCount
    ? Math.round((totals.sent || 0) / activeDayCount)
    : 0;
  const rowsNewestFirst = [...activeDays].reverse();

  return (
    <div className="watchlist-timeline">
      <div className="watchlist-timeline-summary">
        <div className="watchlist-tl-chip">
          <span className="watchlist-tl-chip-val">{formatDuration(totals.activeSeconds)}</span>
          <span className="watchlist-tl-chip-lbl">Aktiv gesamt</span>
        </div>
        <div className="watchlist-tl-chip">
          <span className="watchlist-tl-chip-val">{totals.sent || 0}</span>
          <span className="watchlist-tl-chip-lbl">Gesendet gesamt</span>
        </div>
        <div className="watchlist-tl-chip">
          <span className="watchlist-tl-chip-val">{activeDayCount}<small>/{days.length}</small></span>
          <span className="watchlist-tl-chip-lbl">Aktive Tage</span>
        </div>
        <div className="watchlist-tl-chip">
          <span className="watchlist-tl-chip-val">{formatDuration(avgActivePerActiveDay)}</span>
          <span className="watchlist-tl-chip-lbl">Ø Aktiv / Tag</span>
        </div>
        <div className="watchlist-tl-chip">
          <span className="watchlist-tl-chip-val">{avgSentPerActiveDay}</span>
          <span className="watchlist-tl-chip-lbl">Ø Gesendet / Tag</span>
        </div>
      </div>

      <div className="watchlist-timeline-charts">
        <div className="watchlist-acct-timeline-block">
          <div className="watchlist-section-label">Aktivzeit / Tag ({WINDOW_DAYS}d)</div>
          <ActivityBars days={days} metric="activeSeconds" />
        </div>
        <div className="watchlist-acct-timeline-block">
          <div className="watchlist-section-label">Gesendet / Tag ({WINDOW_DAYS}d)</div>
          <ActivityBars days={days} metric="sent" />
        </div>
      </div>

      <div className="watchlist-timeline-note">
        <i className="fa fa-circle-info"></i>
        Versandzahlen = nur manuell gesendete Mails. Automatische Outreach-Jobs &amp;
        Flow-Automation werden hier nicht mitgezählt.
      </div>

      <div className="watchlist-timeline-breakdown">
        <div className="watchlist-section-label">
          Tagesaufschlüsselung — Tag anklicken für Account-Details
        </div>
        {rowsNewestFirst.length === 0 ? (
          <div className="watchlist-acct-sessions-empty">
            Keine Aktivität in den letzten {WINDOW_DAYS} Tagen.
          </div>
        ) : (
          <div className="watchlist-day-list">
            {rowsNewestFirst.map((d) => (
              <DayBreakdownRow key={d.date} day={d} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountRow({ account }) {
  const [expanded, setExpanded] = useState(false);
  const t = account.totals || {};
  const today = account.today || {};
  const roleLabel = ROLE_LABELS[account.role] || 'Member';

  return (
    <div className={`watchlist-acct ${expanded ? 'watchlist-acct-open' : ''}`}>
      <button className="watchlist-acct-head" onClick={() => setExpanded((v) => !v)}>
        <div className="watchlist-acct-identity">
          <i className={`fa fa-chevron-${expanded ? 'down' : 'right'} watchlist-acct-chevron`}></i>
          <div className="watchlist-acct-name-wrap">
            <span className="watchlist-acct-name">{account.worldName || account.worldUid}</span>
            <span className={`watchlist-acct-role watchlist-acct-role-${account.isOwn ? 'own' : 'member'}`}>
              {roleLabel}
            </span>
          </div>
        </div>
        <div className="watchlist-acct-quickstats">
          <div className="watchlist-acct-quick">
            <span className="watchlist-acct-quick-val">{t.sent || 0}</span>
            <span className="watchlist-acct-quick-lbl">gesendet</span>
          </div>
          <div className="watchlist-acct-quick">
            <span className="watchlist-acct-quick-val">{formatDuration(t.activeSeconds)}</span>
            <span className="watchlist-acct-quick-lbl">aktiv</span>
          </div>
          <div className="watchlist-acct-quick">
            <span className="watchlist-acct-quick-val">{t.sessionCount || 0}</span>
            <span className="watchlist-acct-quick-lbl">Sessions</span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="watchlist-acct-body">
          <div className="watchlist-acct-today">
            <span className="watchlist-acct-today-chip">
              Heute: <b>{today.sent || 0}</b> Mails · <b>{formatDuration(today.activeSeconds)}</b> aktiv · <b>{today.sessionCount || 0}</b> Sessions
            </span>
          </div>

          <div className="watchlist-acct-timeline">
            <div className="watchlist-acct-timeline-block">
              <div className="watchlist-section-label">Gesendet / Tag ({WINDOW_DAYS}d)</div>
              <ActivityBars days={account.days} metric="sent" />
            </div>
            <div className="watchlist-acct-timeline-block">
              <div className="watchlist-section-label">Aktivzeit / Tag ({WINDOW_DAYS}d)</div>
              <ActivityBars days={account.days} metric="activeSeconds" />
            </div>
          </div>

          <div className="watchlist-acct-sessions">
            <div className="watchlist-section-label">Letzte Sessions</div>
            {(account.recentSessions || []).length === 0 ? (
              <div className="watchlist-acct-sessions-empty">Noch keine Sessions erfasst.</div>
            ) : (
              <ul className="watchlist-session-list">
                {account.recentSessions.map((s, i) => (
                  <li key={i} className="watchlist-session-item">
                    <span className="watchlist-session-when">{formatSessionRange(s.start, s.end)}</span>
                    <span className="watchlist-session-dur">{formatDuration(s.durationSeconds)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PersonCard({ item, stats, isLoading, onRemove, onEditTarget }) {
  const [editingTarget, setEditingTarget] = useState(false);
  const [draftTarget, setDraftTarget] = useState(item.dailyTarget);
  const [savingTarget, setSavingTarget] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  useEffect(() => {
    setDraftTarget(item.dailyTarget);
  }, [item.dailyTarget]);

  const accounts = stats?.accounts || [];
  const totals = stats?.totals || { sent: 0, activeSeconds: 0, sessionCount: 0 };
  const todaySent = stats?.today?.sent || 0;
  const todayActiveSeconds = accounts.reduce((a, acc) => a + (acc.today?.activeSeconds || 0), 0);
  const todaySessions = accounts.reduce((a, acc) => a + (acc.today?.sessionCount || 0), 0);
  const activeAccounts = accounts.filter(
    (a) => (a.totals?.sent || 0) > 0 || (a.totals?.activeSeconds || 0) > 0
  );

  // Per-day totals across ALL accounts (for the 14-day platform timeline),
  // keeping a per-account split so we can break each day down by category.
  const aggregatedDays = useMemo(() => {
    const byDate = new Map();
    accounts.forEach((acc) => {
      (acc.days || []).forEach((d) => {
        const cur = byDate.get(d.date) || {
          date: d.date,
          sent: 0,
          activeSeconds: 0,
          sessionCount: 0,
          byAccount: [],
        };
        cur.sent += d.sent || 0;
        cur.activeSeconds += d.activeSeconds || 0;
        cur.sessionCount += d.sessionCount || 0;
        if ((d.sent || 0) > 0 || (d.activeSeconds || 0) > 0) {
          cur.byAccount.push({
            worldUid: acc.worldUid,
            worldName: acc.worldName || acc.worldUid,
            isOwn: acc.isOwn,
            role: acc.role,
            sent: d.sent || 0,
            activeSeconds: d.activeSeconds || 0,
            sessionCount: d.sessionCount || 0,
          });
        }
        byDate.set(d.date, cur);
      });
    });
    return Array.from(byDate.values())
      .map((d) => ({
        ...d,
        byAccount: d.byAccount.sort((a, b) => b.activeSeconds - a.activeSeconds),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [accounts]);

  const status = classifyProgress(todaySent, item.dailyTarget);
  const delta = todaySent - item.dailyTarget;
  const pct = item.dailyTarget > 0 ? Math.round((todaySent / item.dailyTarget) * 100) : 0;

  const saveTarget = async () => {
    const next = Math.max(0, parseInt(draftTarget, 10) || 0);
    if (next === item.dailyTarget) {
      setEditingTarget(false);
      return;
    }
    setSavingTarget(true);
    try {
      await onEditTarget(item.id, next);
      setEditingTarget(false);
    } finally {
      setSavingTarget(false);
    }
  };

  return (
    <div className={`watchlist-owner-card watchlist-owner-status-${status}`}>
      <div className="watchlist-owner-card-head">
        <div className="watchlist-owner-card-identity">
          <div className="watchlist-owner-avatar">
            {(item.ownerEmail || '?')[0].toUpperCase()}
          </div>
          <div className="watchlist-owner-meta">
            <div className="watchlist-owner-email">
              {item.ownerEmail || item.ownerUserId}
            </div>
            <div className="watchlist-owner-sub">
              {item.ownerName ? `${item.ownerName} · ` : ''}
              {activeAccounts.length}/{accounts.length} Accounts aktiv
            </div>
          </div>
        </div>
        <div className="watchlist-owner-card-actions">
          <button
            className="watchlist-icon-btn"
            onClick={() => setEditingTarget(true)}
            title="Tagesziel bearbeiten"
          >
            <i className="fa fa-pen"></i>
          </button>
          <button
            className="watchlist-icon-btn watchlist-icon-btn-danger"
            onClick={() => onRemove(item)}
            title="Von der Watchlist entfernen"
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
      </div>

      <div className="watchlist-owner-card-body">
        <div
          className="watchlist-owner-card-gauge"
          title="Tagesziel = manuell gesendete Mails über alle Accounts. Automatische Outreach-Jobs & Flows zählen nicht mit."
        >
          <ProgressDonut sent={todaySent} target={item.dailyTarget} />
          <div className="watchlist-owner-card-gauge-meta">
            <div className={`watchlist-delta watchlist-delta-${status}`}>
              {delta >= 0 ? `+${delta}` : delta} vs. Ziel · {pct}%
            </div>
            <div className="watchlist-card-target-row">
              {editingTarget ? (
                <div className="watchlist-target-edit">
                  <input
                    type="number"
                    min="0"
                    value={draftTarget}
                    onChange={(e) => setDraftTarget(e.target.value)}
                  />
                  <button onClick={saveTarget} disabled={savingTarget}>
                    {savingTarget ? '…' : 'OK'}
                  </button>
                  <button
                    onClick={() => { setEditingTarget(false); setDraftTarget(item.dailyTarget); }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  className="watchlist-target-btn"
                  onClick={() => setEditingTarget(true)}
                  title="Tagesziel anpassen"
                >
                  <i className="fa fa-bullseye"></i> Ziel: {item.dailyTarget}/Tag
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="watchlist-person-metrics">
          <div className="watchlist-metric">
            <div className="watchlist-metric-icon"><i className="fa fa-clock"></i></div>
            <div className="watchlist-metric-body">
              <div className="watchlist-metric-value">{formatDuration(todayActiveSeconds)}</div>
              <div className="watchlist-metric-label">Aktiv heute</div>
            </div>
          </div>
          <div className="watchlist-metric">
            <div className="watchlist-metric-icon"><i className="fa fa-bolt"></i></div>
            <div className="watchlist-metric-body">
              <div className="watchlist-metric-value">{todaySessions}</div>
              <div className="watchlist-metric-label">Sessions heute</div>
            </div>
          </div>
          <div className="watchlist-metric">
            <div className="watchlist-metric-icon"><i className="fa fa-hourglass-half"></i></div>
            <div className="watchlist-metric-body">
              <div className="watchlist-metric-value">{formatDuration(totals.activeSeconds)}</div>
              <div className="watchlist-metric-label">Aktiv {WINDOW_DAYS}d</div>
            </div>
          </div>
          <div
            className="watchlist-metric"
            title="Nur manuell gesendete Mails — automatische Outreach-Jobs & Flows sind nicht enthalten."
          >
            <div className="watchlist-metric-icon"><i className="fa fa-paper-plane"></i></div>
            <div className="watchlist-metric-body">
              <div className="watchlist-metric-value">{totals.sent}</div>
              <div className="watchlist-metric-label">Gesendet {WINDOW_DAYS}d</div>
            </div>
          </div>
        </div>
      </div>

      <div className="watchlist-timeline-toggle-row">
        <button
          className={`watchlist-timeline-toggle ${showTimeline ? 'watchlist-timeline-toggle-on' : ''}`}
          onClick={() => setShowTimeline((v) => !v)}
        >
          <i className={`fa fa-chevron-${showTimeline ? 'down' : 'right'}`}></i>
          <i className="fa fa-chart-column"></i>
          <span>Verlauf {WINDOW_DAYS} Tage — Aktivzeit &amp; Versand (alle Accounts)</span>
        </button>
      </div>

      {showTimeline && (
        <PersonTimeline days={aggregatedDays} totals={totals} />
      )}

      <div className="watchlist-person-accounts">
        <div className="watchlist-section-label">
          Accounts ({accounts.length}) — Mitgliedschaften &amp; Aktivität
        </div>
        {accounts.length === 0 ? (
          <div className="watchlist-acct-sessions-empty">
            {isLoading ? 'Lade…' : 'Keine Mitgliedschaften / Aktivität gefunden.'}
          </div>
        ) : (
          accounts.map((acc) => <AccountRow key={acc.worldUid} account={acc} />)
        )}
      </div>

      {isLoading && (
        <div className="watchlist-card-loading-overlay">
          <i className="fa fa-spinner fa-spin"></i>
        </div>
      )}
    </div>
  );
}

function AddOwnerModal({ open, onClose, onAdd, existingOwnerIds }) {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [dailyTarget, setDailyTarget] = useState(150);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedOwner(null);
    setDailyTarget(150);
    setError('');
  }, [open]);

  useEffect(() => {
    if (!open || users.length > 0) return;
    setUsersLoading(true);
    (async () => {
      try {
        const snap = await get(dbRef(database, 'Users'));
        const data = snap.val() || {};
        const arr = Object.entries(data)
          .map(([id, u]) => ({
            id,
            email: u?.email || '',
            name: u?.name || '',
            companyName: u?.companyName || '',
          }))
          .filter((u) => u.email)
          .sort((a, b) => a.email.localeCompare(b.email));
        setUsers(arr);
      } catch (err) {
        setError('Konnte User-Liste nicht laden: ' + (err.message || err));
      } finally {
        setUsersLoading(false);
      }
    })();
  }, [open, users.length]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users.slice(0, 100);
    const q = search.toLowerCase();
    return users
      .filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          u.name.toLowerCase().includes(q) ||
          u.companyName.toLowerCase().includes(q)
      )
      .slice(0, 100);
  }, [users, search]);

  const submit = async () => {
    setError('');
    if (!selectedOwner) return setError('Bitte eine Person auswählen.');
    if (!dailyTarget || dailyTarget < 1) return setError('Tagesziel muss > 0 sein.');
    if (existingOwnerIds.has(selectedOwner.id)) {
      return setError('Diese Person ist bereits in deiner Watchlist.');
    }

    setSubmitting(true);
    try {
      await onAdd({
        ownerUserId: selectedOwner.id,
        ownerEmail: selectedOwner.email,
        ownerName: selectedOwner.name || null,
        dailyTarget: Number(dailyTarget),
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="watchlist-modal-overlay" onClick={onClose}>
      <div className="watchlist-modal" onClick={(e) => e.stopPropagation()}>
        <div className="watchlist-modal-header">
          <h3><i className="fa fa-eye"></i> Person zur Watchlist hinzufügen</h3>
          <button className="watchlist-modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="watchlist-modal-error">{error}</div>}

        <div className="watchlist-modal-body">
          <div className="watchlist-modal-section-label">Person auswählen</div>
          <input
            type="text"
            className="watchlist-search"
            placeholder="Suchen (Email, Name, Firma)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="watchlist-modal-list">
            {usersLoading && (
              <div className="watchlist-modal-loading">
                <i className="fa fa-spinner fa-spin"></i> Lade Personen…
              </div>
            )}
            {!usersLoading && filteredUsers.length === 0 && (
              <div className="watchlist-modal-empty">Keine Treffer.</div>
            )}
            {!usersLoading && filteredUsers.map((u) => {
              const alreadyAdded = existingOwnerIds.has(u.id);
              return (
                <button
                  key={u.id}
                  className={`watchlist-user-row ${selectedOwner?.id === u.id ? 'watchlist-user-row-active' : ''}`}
                  onClick={() => !alreadyAdded && setSelectedOwner(u)}
                  disabled={alreadyAdded}
                  title={alreadyAdded ? 'Bereits in der Watchlist' : ''}
                >
                  <div className="watchlist-user-avatar">{u.email[0].toUpperCase()}</div>
                  <div className="watchlist-user-meta">
                    <div className="watchlist-user-email">{u.email}</div>
                    {(u.name || u.companyName) && (
                      <div className="watchlist-user-sub">{u.name || u.companyName}</div>
                    )}
                  </div>
                  {alreadyAdded && <span className="watchlist-user-row-tag">drin</span>}
                  {!alreadyAdded && selectedOwner?.id === u.id && <i className="fa fa-check"></i>}
                </button>
              );
            })}
          </div>

          <div className="watchlist-modal-section-label" style={{ marginTop: '14px' }}>
            Tagesziel (gesendete Mails / Tag — über alle Accounts)
          </div>
          <div className="watchlist-target-input-row">
            <input
              type="number"
              min="1"
              className="watchlist-target-input"
              value={dailyTarget}
              onChange={(e) => setDailyTarget(e.target.value)}
            />
            <div className="watchlist-target-presets">
              {[50, 100, 200, 500, 1000].map((p) => (
                <button
                  key={p}
                  onClick={() => setDailyTarget(p)}
                  className={Number(dailyTarget) === p ? 'active' : ''}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="watchlist-modal-actions">
            <button className="watchlist-btn-ghost" onClick={onClose}>Abbrechen</button>
            <button
              className="watchlist-btn-primary"
              onClick={submit}
              disabled={submitting || !selectedOwner}
            >
              {submitting ? (
                <><i className="fa fa-spinner fa-spin"></i> Speichern…</>
              ) : (
                <><i className="fa fa-plus"></i> Zur Watchlist hinzufügen</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActivityWatchlist() {
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const pollTimerRef = useRef(null);
  const isVisibleRef = useRef(true);
  const itemsRef = useRef([]);

  const adminUid = auth.currentUser?.uid;

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (!adminUid) return;
    const itemsCol = collection(firestore, 'admin_watchlists', adminUid, 'items');
    const q = query(itemsCol, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const seen = new Set();
        const next = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          if (!data?.ownerUserId) return;
          if (seen.has(data.ownerUserId)) return;
          seen.add(data.ownerUserId);
          next.push({ id: d.id, ...data });
        });
        setItems(next);
        setItemsLoading(false);
      },
      (err) => {
        console.error('[Watchlist] items snapshot error:', err);
        setError(err.message || String(err));
        setItemsLoading(false);
      }
    );
    return () => unsub();
  }, [adminUid]);

  const refreshStats = useCallback(async () => {
    const current = itemsRef.current;
    if (current.length === 0) {
      setStats({});
      return;
    }
    setStatsLoading(true);
    try {
      const fn = httpsCallable(functions, 'getMemberActivityStats');
      const payload = current.map((it) => ({
        itemId: it.id,
        personUid: it.ownerUserId,
        dailyTarget: it.dailyTarget,
      }));
      const res = await fn({ items: payload, days: WINDOW_DAYS });
      setStats(res.data?.stats || {});
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('[Watchlist] stats refresh failed:', err);
      setError(err.message || 'Stats konnten nicht geladen werden');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (itemsLoading) return;
    refreshStats();
  }, [itemsLoading, items.length, refreshStats]);

  useEffect(() => {
    const onVisibility = () => {
      isVisibleRef.current = document.visibilityState === 'visible';
      if (isVisibleRef.current) refreshStats();
    };
    document.addEventListener('visibilitychange', onVisibility);

    pollTimerRef.current = setInterval(() => {
      if (isVisibleRef.current) refreshStats();
    }, POLL_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [refreshStats]);

  const handleAdd = async (payload) => {
    if (!adminUid) throw new Error('Nicht eingeloggt');
    const itemsCol = collection(firestore, 'admin_watchlists', adminUid, 'items');
    await addDoc(itemsCol, {
      ...payload,
      addedBy: adminUid,
      createdAt: serverTimestamp(),
    });
  };

  const handleRemove = async (item) => {
    if (!adminUid) return;
    if (!window.confirm(`"${item.ownerEmail}" aus deiner Watchlist entfernen?`)) return;
    try {
      await deleteDoc(doc(firestore, 'admin_watchlists', adminUid, 'items', item.id));
    } catch (err) {
      setError(err.message || 'Entfernen fehlgeschlagen');
    }
  };

  const handleEditTarget = async (itemId, nextTarget) => {
    if (!adminUid) return;
    await updateDoc(doc(firestore, 'admin_watchlists', adminUid, 'items', itemId), {
      dailyTarget: nextTarget,
    });
    setTimeout(refreshStats, 200);
  };

  // Worst performers first (today's sent vs target).
  const sortedItems = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const sa = stats[a.id]?.today?.sent || 0;
      const sb = stats[b.id]?.today?.sent || 0;
      const ta = a.dailyTarget || 1;
      const tb = b.dailyTarget || 1;
      return sa / ta - sb / tb;
    });
    return arr;
  }, [items, stats]);

  const headerStats = useMemo(() => {
    let todaySent = 0;
    let totalTarget = 0;
    let activeToday = 0;
    items.forEach((it) => {
      const s = stats[it.id];
      const sent = s?.today?.sent || 0;
      todaySent += sent;
      totalTarget += it.dailyTarget || 0;
      const activeSeconds = (s?.accounts || []).reduce(
        (a, acc) => a + (acc.today?.activeSeconds || 0),
        0
      );
      if (activeSeconds > 0 || sent > 0) activeToday += 1;
    });
    const pct = totalTarget > 0 ? Math.round((todaySent / totalTarget) * 100) : 0;
    return { todaySent, totalTarget, activeToday, pct };
  }, [items, stats]);

  const existingOwnerIds = useMemo(
    () => new Set(items.map((it) => it.ownerUserId)),
    [items]
  );

  return (
    <div className="watchlist-container">
      <div className="watchlist-header">
        <div className="watchlist-header-titles">
          <h2><i className="fa fa-eye"></i> Activity Watchlist</h2>
          <p>
            Pro Person: in welchen Accounts sie Mitglied ist, wie viele Mails sie dort
            <b> manuell</b> sendet, plus echte Aktivzeit, Sessions und Timeline der letzten {WINDOW_DAYS} Tage.
            Versandzahlen erfassen nur menschlich ausgelöste Sends — automatische Outreach-Jobs
            &amp; Flow-Automation sind nicht enthalten.
          </p>
        </div>

        <div className="watchlist-header-stats">
          <div
            className="watchlist-header-stat"
            title="Nur manuell gesendete Mails — automatische Outreach-Jobs & Flows sind nicht enthalten."
          >
            <span className="watchlist-header-stat-label">Heute gesendet (manuell)</span>
            <span className="watchlist-header-stat-value">
              {headerStats.todaySent}
              <span className="watchlist-header-stat-sub">/ {headerStats.totalTarget}</span>
            </span>
            <div className="watchlist-header-bar">
              <div
                className={`watchlist-header-bar-fill watchlist-header-bar-${
                  headerStats.pct >= 100 ? 'green' : headerStats.pct >= 80 ? 'yellow' : 'red'
                }`}
                style={{ width: `${Math.min(headerStats.pct, 100)}%` }}
              />
            </div>
          </div>
          <div className="watchlist-header-stat">
            <span className="watchlist-header-stat-label">Aktiv heute</span>
            <span className="watchlist-header-stat-value">
              {headerStats.activeToday}
              <span className="watchlist-header-stat-sub">/ {items.length}</span>
            </span>
          </div>
          <button
            className="watchlist-add-btn"
            onClick={() => setShowAdd(true)}
            title="Person zur Watchlist hinzufügen"
          >
            <i className="fa fa-plus"></i> Person hinzufügen
          </button>
        </div>
      </div>

      {error && (
        <div className="watchlist-banner watchlist-banner-error">
          <i className="fa fa-exclamation-triangle"></i> {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="watchlist-toolbar">
        <button
          className="watchlist-refresh-btn"
          onClick={refreshStats}
          disabled={statsLoading || items.length === 0}
        >
          <i className={`fa fa-sync ${statsLoading ? 'fa-spin' : ''}`}></i>
          {statsLoading ? 'Aktualisiere…' : 'Aktualisieren'}
        </button>
        <span className="watchlist-toolbar-meta">
          {lastRefreshed
            ? `Stand: ${lastRefreshed.toLocaleTimeString('de-DE')}`
            : '—'}
          <span className="watchlist-toolbar-poll">· Auto-Refresh alle 60s</span>
        </span>
      </div>

      {itemsLoading ? (
        <div className="watchlist-empty">
          <i className="fa fa-spinner fa-spin"></i> Lade Watchlist…
        </div>
      ) : items.length === 0 ? (
        <div className="watchlist-empty">
          <div className="watchlist-empty-icon">👀</div>
          <h3>Noch niemand auf deiner Watchlist</h3>
          <p>
            Füge eine Person hinzu und setze ein tägliches Sendeziel. Wir zeigen dir,
            in welchen Accounts sie aktiv ist, wie viel sie sendet und wie lange sie
            wirklich auf der Plattform war.
          </p>
          <button className="watchlist-btn-primary" onClick={() => setShowAdd(true)}>
            <i className="fa fa-plus"></i> Erste Person hinzufügen
          </button>
        </div>
      ) : (
        <div className="watchlist-grid">
          {sortedItems.map((it) => (
            <PersonCard
              key={it.id}
              item={it}
              stats={stats[it.id]}
              isLoading={statsLoading && !stats[it.id]}
              onRemove={handleRemove}
              onEditTarget={handleEditTarget}
            />
          ))}
        </div>
      )}

      <AddOwnerModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onAdd={handleAdd}
        existingOwnerIds={existingOwnerIds}
      />
    </div>
  );
}

export default ActivityWatchlist;
